import React, { useState } from 'react';
import { fitLevel, barFillClass, barTextClass, FIT_TEXT } from '../../lib/fit';
import { EvidenceChip, EvidenceState } from './EvidenceChip';

export interface ScoreFactor {
  category: string;
  score: number;
  weight: number;
  description?: string;
  /** Optional explicit evidence state; otherwise derived from the score. */
  evidenceState?: EvidenceState;
  /** Optional short evidence claim; otherwise a state label is used. */
  evidenceLabel?: string;
}

interface FactorBreakdownProps {
  factors: ScoreFactor[];
  totalScore: number;
  defaultOpen?: boolean;
}

// A factor with profile-backed evidence reads "grounded"; a partial one
// "inferred"; an unsupported one "gap". Derived from the factor score so the
// UI stays correct whatever the engine returns.
function deriveState(score: number): EvidenceState {
  if (score >= 70) return 'grounded';
  if (score >= 40) return 'inferred';
  return 'gap';
}

const STATE_FALLBACK_LABEL: Record<EvidenceState, string> = {
  grounded: 'Grounded in profile',
  inferred: 'Inferred',
  gap: 'No evidence',
};

/**
 * Factor breakdown — the drill-down, not the headline. Collapsed by default
 * ("▸ Why this score"). Renders whatever factors the engine returns: each with
 * its weight, a calm bar in the fit-ramp colors, and an Evidence Chip showing
 * what it traced to. The factor set is never hardcoded.
 */
export function FactorBreakdown({ factors, totalScore, defaultOpen = false }: FactorBreakdownProps) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="border border-bw-border rounded-xl overflow-hidden mb-2">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full flex items-center justify-between px-3.5 py-2.5 bg-bw-surface hover:bg-bw-surface-subtle transition-colors"
      >
        <span className="text-[12.5px] font-semibold text-bw-ink">
          {open ? '▾' : '▸'} Why this score
        </span>
        <span className={`font-mono text-[12px] font-medium ${FIT_TEXT[fitLevel(totalScore)]}`}>
          {Math.round(totalScore)}
        </span>
      </button>

      {open && (
        <div className="p-3 bg-bw-surface-subtle border-t border-bw-border space-y-3 animate-fade-in">
          {factors.length === 0 && (
            <p className="text-[12px] text-bw-muted">
              Complete your company profile to see the factor breakdown.
            </p>
          )}
          {factors.map((f, i) => {
            const state = f.evidenceState ?? deriveState(f.score);
            return (
              <div key={i}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[11.5px] font-medium text-bw-ink">{f.category}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-bw-muted">{f.weight}% wt</span>
                    <span className={`font-mono text-[11.5px] font-medium ${barTextClass(f.score)}`}>{f.score}%</span>
                  </div>
                </div>
                <div className="w-full h-[3px] bg-fit-track rounded-full overflow-hidden mb-1.5">
                  <div
                    className={`h-full rounded-full ${barFillClass(f.score)}`}
                    style={{ width: `${Math.max(0, Math.min(100, f.score))}%`, transition: 'width 0.6s ease' }}
                  />
                </div>
                <EvidenceChip state={state} title={f.description}>
                  {f.evidenceLabel ?? STATE_FALLBACK_LABEL[state]}
                </EvidenceChip>
                {f.description && (
                  <p className="text-[11px] text-bw-muted leading-snug mt-1.5">{f.description}</p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default FactorBreakdown;
